import React, { useCallback, useEffect, useMemo, useState } from 'react';

import isPdf from '@lyearn/core/utils/isPdf';
import { useFollowInstructorContext, useNodePerformanceContext } from '@lyearn/lygo-core';

import ReactPdf from '../../../components/ReactPdf';
import { MAX_PROGRESS } from '../../../components/ReactPdf/consts';
import { NODE_NAME } from '../consts';
import { EmbedComponentProps } from '../types';
import Iframe from './components/Iframe';

const EmbedComponentReadOnly: React.FC<EmbedComponentProps> = (props) => {
  const { href, id, name, embedUrl, aspectRatio, srcDoc, type } = props;
  const [progress, setProgress] = useState(0);

  const { setNodePerformance, getNodePerformance } = useNodePerformanceContext();
  const { setFollowState, followState } = useFollowInstructorContext();

  // { id: { curPage, fullScreen } }

  const setPerformance = useCallback(
    (progress: number) => {
      id && setNodePerformance?.({ id: id, blockType: NODE_NAME, progress });
    },
    [id, setNodePerformance],
  );

  const blockProgress = useMemo(() => {
    if (id) {
      return getNodePerformance?.(id)?.progress || 0;
    }
    return 0;
  }, [id, getNodePerformance]);

  useEffect(() => {
    if (blockProgress) {
      setProgress(blockProgress);
    }
  }, [blockProgress]);

  // send perf for empty block
  useEffect(() => {
    if (progress !== MAX_PROGRESS && !href && !embedUrl && !srcDoc) {
      setPerformance(MAX_PROGRESS);
    }
    // eslint-disable-next-line
  }, [embedUrl, href, setPerformance, srcDoc]);

  const onIframeLoad = useCallback(() => {
    setPerformance(MAX_PROGRESS);
  }, [setPerformance]);

  const onDocumentLoadSuccess = useCallback(
    (numPages: number) => {
      if (numPages === 1) {
        setPerformance(MAX_PROGRESS);
        setProgress(MAX_PROGRESS);
      }
    },
    [setPerformance],
  );

  const onNextPageClick = useCallback(
    (updatedProgress: number) => {
      if (updatedProgress > progress) {
        setPerformance?.(updatedProgress);
        setProgress(updatedProgress);
      }
    },
    [progress, setPerformance],
  );

  const pageChangeCallback = useCallback(
    (newBlockState: object) => {
      setFollowState?.(id!, newBlockState);
    },
    [id, setFollowState],
  );

  if (!href && !embedUrl && !srcDoc) {
    return null;
  }

  if (type === 'pdf' || (href && isPdf(href)) || (embedUrl && isPdf(embedUrl))) {
    return (
      <div className="my-48">
        <ReactPdf
          showProgress
          aspectRatio={aspectRatio}
          documentLoadSuccessCallback={onDocumentLoadSuccess}
          followFullscreen={
            followState && followState[id!] ? (followState[id!] as any).followFullscreen : undefined
          }
          followPage={
            followState && followState[id!] ? (followState[id!] as any).followPage : undefined
          }
          initialProgress={blockProgress}
          name={name?.split('.pdf')[0]}
          nextPageClickCallback={onNextPageClick}
          pageChangeCallback={setFollowState ? pageChangeCallback : undefined}
          progress={progress}
          src={href ? href : embedUrl!}
        />
      </div>
    );
  }

  if (embedUrl || srcDoc) {
    return <Iframe src={embedUrl} srcDoc={srcDoc} onLoad={onIframeLoad} />;
  }

  return null;
};

export default EmbedComponentReadOnly;
